import { firebaseDb } from "../firebase/client-app";
import {
  DbBusinessWaitlist,
  DbWaitlistAdditionalInfo,
  DbWaitlistState,
} from "./models";

import {
  collection,
  Firestore,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  limit,
  orderBy,
  Timestamp,
  getCount,
  updateDoc,
  startAfter,
  getAggregate,
  sum,
} from "firebase/firestore/lite";
import { DbWaitlistBooking, DbWaitlistBookingStatus } from "./models";
import { DateTime } from "luxon";
import { axiosInstance } from "../rest/axios";
import { getCookie } from "cookies-next";
import { DbBusinessReview } from "../business/models";

export class WaitlistService {
  private waitlistBookingRef(firestore: Firestore) {
    return collection(firestore, "waitlist_booking");
  }

  private waitlistRef(firestore: Firestore, businessId: string) {
    return collection(
      firestore,
      "businesses/" + businessId + "/business_waitlist",
    );
  }

  async addReview(
    bookingId: string,
    data: DbBusinessReview,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    await updateDoc(doc(this.waitlistBookingRef(firestore), bookingId), {
      review: data,
    });
  }

  async getPendingWaitlistBooking(
    userId: string,
    waitlistId: string,
    businessId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbWaitlistBooking | null> {
    const bookings = await getDocs(
      query(
        this.waitlistBookingRef(firestore),
        where("user_id", "==", userId),
        where("business_id", "==", businessId),
        where("waitlist_id", "==", waitlistId),
        where("status", "==", DbWaitlistBookingStatus.pending),
        where(
          "created_at",
          ">=",
          Timestamp.fromDate(DateTime.now().startOf("day").toJSDate()),
        ),
        orderBy("created_at", "desc"),
        limit(1),
      ),
    );

    if (bookings.docs.length === 0) {
      return null;
    }

    return bookings.docs[0].data() as DbWaitlistBooking;
  }

  async getWaitlistBookingsByUserId(
    userId: string,
    firestore: Firestore = firebaseDb,
    duration?: string | null,
  ): Promise<DbWaitlistBooking[]> {
    let bookingQuery = query(
      this.waitlistBookingRef(firestore),
      where("user_id", "==", userId),
      orderBy("created_at", "desc"),
    );

    if (duration !== null && duration !== undefined) {
      const operator = duration === "recent" ? ">=" : "<";
      bookingQuery = query(
        bookingQuery,
        where(
          "created_at",
          operator,
          Timestamp.fromDate(DateTime.now().startOf("day").toJSDate()),
        ),
      );
    }

    const bookings = await getDocs(bookingQuery);
    return bookings.docs.map((doc) => doc.data() as DbWaitlistBooking);
  }

  async getPastWaitlistBookingsOfUser(
    userId: string,
    lastFetchedDate: Date,
    pageLimit: number,
    firestore: Firestore = firebaseDb,
  ): Promise<DbWaitlistBooking[]> {
    let waitlistQuery = query(
      this.waitlistBookingRef(firestore),
      where("user_id", "==", userId),
      orderBy("created_at", "desc"),
      limit(pageLimit),
    );

    if (lastFetchedDate) {
      waitlistQuery = query(waitlistQuery, startAfter(lastFetchedDate));
    }

    const waitlistDocs = await getDocs(waitlistQuery);
    return waitlistDocs.docs.map((doc) => doc.data() as DbWaitlistBooking);
  }

  async getUserPastWaitlistBookingsOfBusiness(
    businessId: string,
    userId: string,
    lastFetchedDate: Date,
    pageLimit: number,
    firestore: Firestore = firebaseDb,
  ): Promise<DbWaitlistBooking[]> {
    let waitlistQuery = query(
      this.waitlistBookingRef(firestore),
      where("business_id", "==", businessId),
      where("user_id", "==", userId),
      orderBy("created_at", "desc"),
      limit(pageLimit),
    );

    if (lastFetchedDate) {
      waitlistQuery = query(waitlistQuery, startAfter(lastFetchedDate));
    }

    const waitlistDocs = await getDocs(waitlistQuery);
    return waitlistDocs.docs.map((doc) => doc.data() as DbWaitlistBooking);
  }

  async checkUserJoinedWaitlist(
    userId: string,
    businessId: string,
    waitlistId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<boolean> {
    const bookingQuery = query(
      this.waitlistBookingRef(firestore),
      where("user_id", "==", userId),
      where("business_id", "==", businessId),
      where("waitlist_id", "==", waitlistId),
      where("status", "in", [
        DbWaitlistState.arrived,
        DbWaitlistState.serving,
        DbWaitlistState.pending,
      ]),
      where(
        "created_at_iso_date",
        "==",
        DateTime.now().startOf("day").toISODate(),
      ),
      limit(1),
    );

    const bookings = await getDocs(bookingQuery);
    if (bookings.docs.map((doc) => doc.data()).length > 0) {
      return true;
    }

    return false;
  }

  async getWaitlistById(
    id: string,
    businessId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessWaitlist | null> {
    const waitlist = await getDoc(
      doc(this.waitlistRef(firestore, businessId), id),
    );

    if (!waitlist.exists()) {
      return null;
    }

    return waitlist.data() as DbBusinessWaitlist;
  }

  async getWaitlistBooking(
    bookingId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbWaitlistBooking | null> {
    const booking = await getDoc(
      doc(this.waitlistBookingRef(firestore), bookingId),
    );

    if (!booking.exists) {
      return null;
    }

    return booking.data() as DbWaitlistBooking;
  }

  async cancelWaitlistBooking(
    businessId: string,
    waitlistBookingId: string,
  ): Promise<DbWaitlistBooking> {
    const res = await axiosInstance.post(
      "waitlist/cancel",
      {
        business_id: businessId,
        waitlist_booking_id: waitlistBookingId,
      },
      { headers: { Authorization: getCookie("Authorization")?.toString() } },
    );

    return res.data;
  }

  async createWaitlistBooking(
    businessId: string,
    waitlistId: string,
    paymentId: string | null,
    userId: string | null,
    guestCount: number | null,
    additionalInfos: DbWaitlistAdditionalInfo[],
  ): Promise<DbWaitlistBooking> {
    const waitlistBooking = {
      business_id: businessId,
      waitlist_id: waitlistId,
      payment_id: paymentId,
      client_id: userId,
      guest_count: guestCount,
      additional_infos: additionalInfos,
    };

    const res = await axiosInstance.post("waitlist/confirm", waitlistBooking, {
      headers: {
        Authorization: getCookie("Authorization")?.toString(),
      },
    });

    return res.data;
  }

  async getWaitlistBookings(
    businessId: string,
    startDate: DateTime | null,
    endDate: DateTime | null,
    firestore: Firestore = firebaseDb,
  ): Promise<DbWaitlistBooking[]> {
    let bookingRef = query(
      this.waitlistBookingRef(firestore),
      where("business_id", "==", businessId),
    );

    if (startDate && endDate) {
      bookingRef = query(
        this.waitlistBookingRef(firestore),
        where("business_id", "==", businessId),
        where("status", "in", [
          DbWaitlistBookingStatus.pending,
          DbWaitlistBookingStatus.arrived,
          DbWaitlistBookingStatus.serving,
          DbWaitlistBookingStatus.completed,
        ]),
        where("created_at", ">=", Timestamp.fromDate(startDate.toJSDate())),
        where("created_at", "<=", Timestamp.fromDate(endDate.toJSDate())),
      );
    }

    const bookings = await getDocs(bookingRef);

    return bookings.docs.map((doc) => doc.data() as DbWaitlistBooking);
  }

  async getWaitlistBookingCounts(
    businessId: string,
    date?: DateTime | null,
    firestore: Firestore = firebaseDb,
  ): Promise<number> {
    let q = query(
      this.waitlistBookingRef(firestore),
      where("business_id", "==", businessId),
      where("status", "in", [
        DbWaitlistBookingStatus.pending,
        DbWaitlistBookingStatus.arrived,
        DbWaitlistBookingStatus.serving,
        DbWaitlistBookingStatus.completed,
      ]),
    );

    if (date) {
      q = query(
        q,
        where("created_at_iso_date", "==", date.startOf("day").toISODate()),
      );
    }

    const waitlistBookings = await getCount(q);
    return waitlistBookings.data().count;
  }

  async getWaitlistBookingsOfDay(
    businessId: string,
    date: DateTime,
    firestore: Firestore = firebaseDb,
  ): Promise<DbWaitlistBooking[]> {
    const startDate = date.startOf("day").toISODate();

    const bookings = query(
      this.waitlistBookingRef(firestore),
      where("business_id", "==", businessId),
      where("status", "in", [
        DbWaitlistBookingStatus.pending,
        DbWaitlistBookingStatus.arrived,
        DbWaitlistBookingStatus.serving,
        DbWaitlistBookingStatus.completed,
      ]),
      where("created_at_iso_date", "==", startDate),
    );

    const bookingDocs = await getDocs(bookings);
    return bookingDocs.docs.map((doc) => doc.data() as DbWaitlistBooking);
  }

  async getUserWaitlistBookingCountsForBusiness(
    businessId: string,
    userId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<number> {
    const bookings = await getCount(
      query(
        this.waitlistBookingRef(firestore),
        where("business_id", "==", businessId),
        where("user_id", "==", userId),
        where("status", "in", [
          DbWaitlistBookingStatus.pending,
          DbWaitlistBookingStatus.arrived,
          DbWaitlistBookingStatus.serving,
          DbWaitlistBookingStatus.completed,
        ]),
      ),
    );

    return bookings.data().count;
  }

  async getCancelledUserWaitlistBookingCountsForBusiness(
    businessId: string,
    userId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<number> {
    const bookings = await getCount(
      query(
        this.waitlistBookingRef(firestore),
        where("business_id", "==", businessId),
        where("user_id", "==", userId),
        where("status", "in", [DbWaitlistBookingStatus.cancelled]),
      ),
    );

    return bookings.data().count;
  }

  async getUserWaitlistBookingsForBusiness(
    businessId: string,
    userId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbWaitlistBooking[]> {
    const bookings = await getDocs(
      query(
        this.waitlistBookingRef(firestore),
        where("business_id", "==", businessId),
        where("user_id", "==", userId),
        where("status", "in", [
          DbWaitlistBookingStatus.pending,
          DbWaitlistBookingStatus.arrived,
          DbWaitlistBookingStatus.serving,
          DbWaitlistBookingStatus.completed,
          DbWaitlistBookingStatus.cancelled,
        ]),
        orderBy("created_at", "desc"),
      ),
    );

    return bookings.docs.map((doc) => doc.data() as DbWaitlistBooking);
  }

  async getWaitlistsByIds(
    businessId: string,
    waitlistIds: string[],
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessWaitlist[]> {
    const waitlists = await getDocs(
      query(
        this.waitlistRef(firestore, businessId),
        where("id", "in", waitlistIds),
      ),
    );

    return waitlists.docs.map((doc) => doc.data() as DbBusinessWaitlist);
  }

  async getWaitlistBookingsRevenue(
    businessId: string,
    date: DateTime,
    firestore: Firestore = firebaseDb,
  ): Promise<number> {
    const startDate = date.startOf("day").toISODate();

    const q = query(
      this.waitlistBookingRef(firestore),
      where("business_id", "==", businessId),
      where("status", "in", [
        DbWaitlistBookingStatus.pending,
        DbWaitlistBookingStatus.arrived,
        DbWaitlistBookingStatus.serving,
        DbWaitlistBookingStatus.completed,
      ]),
      where("created_at_iso_date", "==", startDate),
    );

    const bookingDocs = await getAggregate(q, {
      totalRevenue: sum("deposit_amount"),
    });

    return bookingDocs.data().totalRevenue;
  }
}

export const waitlistService = new WaitlistService();
